// src/components/separation/dashboardstats.tsx

import React from 'react';
import {
  FileText,
  Clock,
  CheckCircle2,
  AlertTriangle,
  ListChecks,
  Wallet,
} from 'lucide-react';
import { DashboardStats } from '../../types/separation';

interface DashboardStatsProps {
  stats: DashboardStats | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

// Static class strings (Tailwind can't see interpolated `bg-${x}` classes, so
// the old dynamic version rendered colourless cards).
const CARD_STYLES: Record<string, { icon: string; value: string }> = {
  blue: { icon: 'bg-blue-100 text-blue-600', value: 'text-blue-600' },
  amber: { icon: 'bg-amber-100 text-amber-600', value: 'text-amber-600' },
  emerald: { icon: 'bg-emerald-100 text-emerald-600', value: 'text-emerald-600' },
  red: { icon: 'bg-red-100 text-red-600', value: 'text-red-600' },
  orange: { icon: 'bg-orange-100 text-orange-600', value: 'text-orange-600' },
  purple: { icon: 'bg-purple-100 text-purple-600', value: 'text-purple-600' },
};

const DashboardStatsComponent: React.FC<DashboardStatsProps> = ({ stats, loading, error, onRefresh }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600 mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Loading dashboard data…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-600 text-sm">{error}</p>
        <button onClick={onRefresh} className="gt-btn is-primary is-sm mt-4">
          Retry
        </button>
      </div>
    );
  }

  if (!stats) return null;

  const cards: {
    title: string;
    value: number;
    color: keyof typeof CARD_STYLES;
    icon: React.ReactNode;
  }[] = [
    { title: 'Total Resignations', value: stats.resignations.total, color: 'blue', icon: <FileText size={20} /> },
    { title: 'Pending Approval', value: stats.resignations.pending_approval, color: 'amber', icon: <Clock size={20} /> },
    { title: 'Completed Resignations', value: stats.resignations.completed, color: 'emerald', icon: <CheckCircle2 size={20} /> },
    { title: 'Total Terminations', value: stats.terminations.total, color: 'red', icon: <AlertTriangle size={20} /> },
    { title: 'Pending Checklists', value: stats.exit_checklists.pending, color: 'orange', icon: <ListChecks size={20} /> },
    { title: 'Pending Settlements', value: stats.settlements.pending, color: 'purple', icon: <Wallet size={20} /> },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {cards.map((card) => {
        const style = CARD_STYLES[card.color];
        return (
          <div
            key={card.title}
            className="bg-white rounded-xl px-5 py-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${style.icon}`}>
                {card.icon}
              </div>
              <div>
                <p className="text-sm text-gray-500">{card.title}</p>
                <p className={`text-2xl font-bold ${style.value}`}>{card.value}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DashboardStatsComponent;
